/**
 * Identity Tab Component
 * Contains Norwegian ID, D-Number, and Personal Info sections
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  User, 
  IdCard,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MapPin,
  Phone
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country data with flags (emoji)
const COUNTRIES = [
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "FI", name: "Finland", flag: "🇫🇮" },
  { code: "PL", name: "Poland", flag: "🇵🇱" },
  { code: "LT", name: "Lithuania", flag: "🇱🇹" },
  { code: "LV", name: "Latvia", flag: "🇱🇻" },
  { code: "EE", name: "Estonia", flag: "🇪🇪" },
  { code: "RO", name: "Romania", flag: "🇷🇴" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "UA", name: "Ukraine", flag: "🇺🇦" },
  { code: "CZ", name: "Czech Republic", flag: "🇨🇿" },
  { code: "SK", name: "Slovakia", flag: "🇸🇰" },
  { code: "HU", name: "Hungary", flag: "🇭🇺" },
  { code: "BG", name: "Bulgaria", flag: "🇧🇬" },
  { code: "HR", name: "Croatia", flag: "🇭🇷" },
  { code: "SI", name: "Slovenia", flag: "🇸🇮" },
  { code: "RS", name: "Serbia", flag: "🇷🇸" },
  { code: "BA", name: "Bosnia", flag: "🇧🇦" },
  { code: "AL", name: "Albania", flag: "🇦🇱" },
  { code: "MK", name: "North Macedonia", flag: "🇲🇰" },
  { code: "TR", name: "Turkey", flag: "🇹🇷" },
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "PK", name: "Pakistan", flag: "🇵🇰" },
  { code: "BD", name: "Bangladesh", flag: "🇧🇩" },
  { code: "PH", name: "Philippines", flag: "🇵🇭" },
  { code: "VN", name: "Vietnam", flag: "🇻🇳" },
  { code: "TH", name: "Thailand", flag: "🇹🇭" },
  { code: "MY", name: "Malaysia", flag: "🇲🇾" },
  { code: "ID", name: "Indonesia", flag: "🇮🇩" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "AR", name: "Argentina", flag: "🇦🇷" },
  { code: "CL", name: "Chile", flag: "🇨🇱" },
  { code: "CO", name: "Colombia", flag: "🇨🇴" },
  { code: "ZA", name: "South Africa", flag: "🇿🇦" },
  { code: "EG", name: "Egypt", flag: "🇪🇬" },
  { code: "NG", name: "Nigeria", flag: "🇳🇬" },
  { code: "KE", name: "Kenya", flag: "🇰🇪" },
  { code: "OTHER", name: "Other", flag: "🌍" },
];

// Helper to get flag for a country
export const getCountryFlag = (nationality) => {
  if (!nationality) return "";
  const country = COUNTRIES.find(c => 
    c.name.toLowerCase() === nationality.toLowerCase() || 
    c.code.toLowerCase() === nationality.toLowerCase()
  );
  return country?.flag || "🌍";
};

export function IdentityTab({ 
  profile, 
  setProfile, 
  user,
  idValidation,
  validatingId,
  handleIdChange,
  handleSubmitNorwegianId,
  handleSubmitDNumber
}) {
  // Auto-save field function
  const autoSaveField = async (field, value) => {
    try {
      await axios.put(`${API}/workers/auto-save-field`, { field, value });
      toast.success(`${field.replace('_', ' ')} saved!`);
    } catch (error) {
      toast.error(`Failed to save ${field}`);
    }
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    setProfile({ ...profile, date_of_birth: value });
    if (value) {
      autoSaveField('date_of_birth', value);
    }
  };

  const handleNationalityChange = (value) => {
    const country = COUNTRIES.find(c => c.code === value);
    const nationalityName = country?.name || value;
    setProfile({ ...profile, nationality: nationalityName });
    autoSaveField('nationality', nationalityName);
  };

  return (
    <div className="space-y-6">
      {/* Profile Completion Banner */}
      {(() => {
        const requiredFields = [
          { key: 'norwegian_id', label: 'Norwegian ID / D-nummer', hasValue: profile.norwegian_id || profile.d_number },
          { key: 'nationality', label: 'Nationality', hasValue: profile.nationality },
          { key: 'date_of_birth', label: 'Date of Birth', hasValue: profile.date_of_birth },
          { key: 'phone', label: 'Phone Number', hasValue: profile.phone },
          { key: 'address', label: 'Address', hasValue: profile.address }
        ];
        const missingFields = requiredFields.filter(f => !f.hasValue);
        const completionPercent = Math.round(((requiredFields.length - missingFields.length) / requiredFields.length) * 100);
        
        if (missingFields.length > 0) {
          return (
            <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-orange-400 font-semibold">Profil {completionPercent}% komplett</p>
                      <span className="text-xs text-orange-300">{missingFields.length} felt gjenstår</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2 mb-3">
                      <div 
                        className="bg-gradient-to-r from-orange-400 to-green-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${completionPercent}%` }}
                      />
                    </div>
                    <p className="text-sm text-[#94A3B8]">
                      Mangler: <span className="text-orange-300">{missingFields.map(f => f.label).join(', ')}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }
        return (
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <p className="text-green-400 font-semibold">Profil 100% komplett! ✓</p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Norwegian ID / D-Number (Combined) */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
            <IdCard className="w-5 h-5 text-[#23F7DD]" />
            Norsk ID (Personnummer / D-nummer)
            {(profile.norwegian_id_verified || profile.d_number_verified) && (
              <CheckCircle2 className="w-5 h-5 text-green-400 ml-2" />
            )}
            {!profile.norwegian_id && !profile.d_number && (
              <span className="ml-auto px-2 py-1 text-xs bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">
                Påkrevd
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#94A3B8]">
            Skriv inn ditt 11-sifrede personnummer eller D-nummer. Systemet gjenkjenner automatisk hvilken type det er.
          </p>
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={profile.norwegian_id || profile.d_number || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    // Update both fields - we'll use norwegian_id as primary
                    handleIdChange(value, 'norwegian_id');
                  }}
                  placeholder="11-sifret personnummer eller D-nummer"
                  maxLength={11}
                  className={`bg-black/50 text-white font-mono text-lg tracking-wider ${
                    !profile.norwegian_id && !profile.d_number 
                      ? 'border-orange-500/50 focus:border-orange-400' 
                      : idValidation.norwegian_id?.valid === true || idValidation.d_number?.valid === true
                        ? 'border-green-500 focus:border-green-400' 
                        : idValidation.norwegian_id?.valid === false || idValidation.d_number?.valid === false
                          ? 'border-red-500 focus:border-red-400' 
                          : 'border-white/10 focus:border-[#23F7DD]'
                  }`}
                  disabled={profile.norwegian_id_verified || profile.d_number_verified}
                  data-testid="norwegian-id-input"
                />
                {(validatingId.norwegian_id || validatingId.d_number) && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="w-4 h-4 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin inline-block"></span>
                  </div>
                )}
              </div>
              {!profile.norwegian_id_verified && !profile.d_number_verified && (profile.norwegian_id || profile.d_number) && 
               (profile.norwegian_id?.length === 11 || profile.d_number?.length === 11) && 
               (idValidation.norwegian_id?.valid || idValidation.d_number?.valid) && (
                <Button 
                  onClick={idValidation.norwegian_id?.type === 'd_number' ? handleSubmitDNumber : handleSubmitNorwegianId}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                >
                  Bekreft
                </Button>
              )}
            </div>
            
            {/* Character count */}
            {(profile.norwegian_id || profile.d_number) && (profile.norwegian_id?.length < 11 && profile.d_number?.length < 11) && (
              <p className="text-xs text-[#94A3B8]">
                {(profile.norwegian_id || profile.d_number || '').length}/11 siffer
              </p>
            )}
            
            {/* Validation Feedback */}
            {(idValidation.norwegian_id || idValidation.d_number) && (
              <div className={`p-3 rounded-lg ${
                (idValidation.norwegian_id?.valid || idValidation.d_number?.valid)
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                {(idValidation.norwegian_id?.valid || idValidation.d_number?.valid) ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-400 font-medium text-sm">
                        Gyldig {(idValidation.norwegian_id?.type || idValidation.d_number?.type) === 'd_number' ? 'D-nummer' : 'Personnummer'}
                      </p>
                      <p className="text-[#94A3B8] text-xs mt-1">
                        Fødselsdato: {idValidation.norwegian_id?.details?.birth_date || idValidation.d_number?.details?.birth_date}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium text-sm">Ugyldig ID-nummer</p>
                      <p className="text-[#94A3B8] text-xs mt-1">
                        {idValidation.norwegian_id?.error || idValidation.d_number?.error || "Vennligst sjekk nummeret og prøv igjen"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {(profile.norwegian_id || profile.d_number) && !profile.norwegian_id_verified && !profile.d_number_verified && 
           (profile.norwegian_id?.length === 11 || profile.d_number?.length === 11) && 
           (idValidation.norwegian_id?.valid || idValidation.d_number?.valid) && (
            <div className="flex items-center gap-2 text-yellow-400 text-sm">
              <Clock className="w-4 h-4" />
              Venter på verifisering av administrator
            </div>
          )}
          
          {(profile.norwegian_id_verified || profile.d_number_verified) && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Verifisert av administrator
            </div>
          )}
        </CardContent>
      </Card>

      {/* Personal Info */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
            <User className="w-5 h-5 text-[#23F7DD]" />
            Personlig Informasjon
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Fullt Navn</Label>
              <Input
                value={user?.name || ""}
                disabled
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">E-post</Label>
              <Input
                value={user?.email || ""}
                disabled
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8] flex items-center gap-2">
                Fødselsdato
                {!profile.date_of_birth && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded border border-orange-500/30">
                    Påkrevd
                  </span>
                )}
              </Label>
              <Input
                type="date"
                value={profile.date_of_birth || ""}
                onChange={handleDateChange}
                className={`bg-black/50 text-white focus:border-[#23F7DD] ${
                  !profile.date_of_birth ? 'border-orange-500/50' : 'border-white/10'
                }`}
                data-testid="date-of-birth-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8] flex items-center gap-2">
                Nasjonalitet
                {!profile.nationality && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded border border-orange-500/30">
                    Påkrevd
                  </span>
                )}
              </Label>
              <Select
                value={COUNTRIES.find(c => c.name === profile.nationality)?.code || ""}
                onValueChange={handleNationalityChange}
              >
                <SelectTrigger 
                  className={`bg-black/50 text-white focus:border-[#23F7DD] ${
                    !profile.nationality ? 'border-orange-500/50' : 'border-white/10'
                  }`}
                  data-testid="nationality-select"
                >
                  <SelectValue placeholder="Velg nasjonalitet">
                    {profile.nationality && (
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{getCountryFlag(profile.nationality)}</span>
                        <span>{profile.nationality}</span>
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-[300px]">
                  {COUNTRIES.map((country) => (
                    <SelectItem 
                      key={country.code} 
                      value={country.code}
                      className="text-white hover:bg-white/10 focus:bg-white/10"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{country.flag}</span>
                        <span>{country.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Phone Number */}
          <div className="space-y-2">
            <Label className="text-[#94A3B8] flex items-center gap-2">
              <Phone className="w-4 h-4" /> Telefon
              {!profile.phone && (
                <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded border border-orange-500/30">
                  Påkrevd
                </span>
              )}
            </Label>
            <Input
              value={profile.phone || ""}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              onBlur={(e) => autoSaveField('phone', e.target.value)}
              placeholder="+47 XXX XX XXX"
              className={`bg-black/50 text-white focus:border-[#23F7DD] ${
                !profile.phone ? 'border-orange-500/50' : 'border-white/10'
              }`}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8] flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Adresse
              {!profile.address && (
                <span className="px-1.5 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded border border-orange-500/30">
                  Påkrevd
                </span>
              )}
            </Label>
            <Input
              value={profile.address || ""}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              onBlur={(e) => autoSaveField('address', e.target.value)}
              placeholder="Gateadresse"
              className={`bg-black/50 text-white focus:border-[#23F7DD] ${
                !profile.address ? 'border-orange-500/50' : 'border-white/10'
              }`}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">By</Label>
              <Input
                value={profile.city || ""}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                onBlur={(e) => autoSaveField('city', e.target.value)}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Postnummer</Label>
              <Input
                value={profile.postal_code || ""}
                onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })}
                onBlur={(e) => autoSaveField('postal_code', e.target.value)}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Nødkontakt Navn</Label>
              <Input
                value={profile.emergency_contact_name || ""}
                onChange={(e) => setProfile({ ...profile, emergency_contact_name: e.target.value })}
                onBlur={(e) => autoSaveField('emergency_contact_name', e.target.value)}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Nødkontakt Telefon</Label>
              <Input
                value={profile.emergency_contact_phone || ""}
                onChange={(e) => setProfile({ ...profile, emergency_contact_phone: e.target.value })}
                onBlur={(e) => autoSaveField('emergency_contact_phone', e.target.value)}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
          </div>

          {/* Email Notifications */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-[#23F7DD]/5 border border-[#23F7DD]/20">
              <input
                type="checkbox"
                id="notify_new_projects"
                checked={profile.notify_new_projects !== false}
                onChange={(e) => {
                  setProfile({ ...profile, notify_new_projects: e.target.checked });
                  autoSaveField('notify_new_projects', e.target.checked);
                }}
                className="mt-1 w-4 h-4 rounded border-[#23F7DD]/30 bg-black/50 text-[#23F7DD] focus:ring-[#23F7DD] focus:ring-offset-0"
              />
              <label htmlFor="notify_new_projects" className="cursor-pointer">
                <span className="text-white font-medium">
                  Varsle meg om nye prosjekter
                </span>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Motta e-post når nye prosjekter blir tilgjengelige for søknad
                </p>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
